import {
  Box,
  Group,
  Textarea as MantineTextarea,
  Text,
  type TextareaProps as MantineTextareaProps,
} from "@mantine/core";
import clsx from "clsx";
import { useId } from "react";
import type { UseFormRegisterReturn } from "react-hook-form";
import styles from "./TextArea.module.scss";
import { FormErrorBox } from "../FormErrorBox/FormErrorBox";

export type TextAreaSize = "small" | "large";

export type TextAreaProps = Omit<MantineTextareaProps, "value" | "onChange" | "error"> & {
  label?: string;
  optional?: boolean;
  required?: boolean;
  hint?: string;
  helpIcon?: React.ReactNode;
  placeholder?: string;
  disabled?: boolean;
  inputClassName?: string;
  size?: TextAreaSize;
  value?: string;
  onChange?: (val: string) => void;
  error?: boolean | string;
  register?: UseFormRegisterReturn;
  className?: string;
  style?: React.CSSProperties;
  dataTestId?: string;
  width?: string | number;
  rows?: number;
  wrapperId?: string;
};

export function TextArea({
  label,
  optional,
  required,
  hint,
  helpIcon,
  placeholder = "Enter",
  disabled,
  inputClassName,
  size,
  value,
  onChange,
  error,
  register,
  className,
  style,
  dataTestId,
  width,
  rows = 4,
  wrapperId,
  ...rest
}: TextAreaProps) {
  const id = useId();

  return (
    <Box
      id={wrapperId}
      className={clsx(styles.wrapper, styles[`wrapper-${size}`], className)}
      w={width}
    >
      {(label || optional) && (
        <Group justify="space-between" align="center" className={styles.labelRow}>
          {label && (
            <Text component="label" htmlFor={id} className={styles.labelText}>
              {label}
              {required && <span style={{ color: "var(--text-danger-default)" }}> *</span>}
              {helpIcon && (
                <Box component="span" className={styles.helpIcon}>
                  {helpIcon}
                </Box>
              )}
            </Text>
          )}
          {optional && <Text className={styles.optionalText}>Optional</Text>}
        </Group>
      )}

      <MantineTextarea
        data-testid={dataTestId}
        id={id}
        value={register ? undefined : value}
        onChange={register ? register.onChange : (e) => onChange?.(e.currentTarget.value)}
        placeholder={placeholder}
        disabled={disabled}
        rows={rows}
        style={style}
        classNames={{
          input: clsx(styles.textarea, error && styles.textareaError, inputClassName),
        }}
        {...register}
        {...rest}
      />

      {typeof error === "string" && <FormErrorBox message={error} mt="6" />}

      {hint && <Text className={styles.hint}>{hint}</Text>}
    </Box>
  );
}
